import { Injectable, inject } from '@angular/core';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { take } from 'rxjs/operators';

/**
 * Shared service to cache and provide user profile colors.
 * Fetches profileColor from each user's Firestore document once,
 * caches the result, and provides it synchronously thereafter.
 *
 * Usage:
 *   const color = this.profileColorService.getColor(uid);
 *   // Returns the cached hex color (e.g. '#e53935') or '' if not yet loaded / not set.
 *
 *   this.profileColorService.ensureLoaded(uid);
 *   // Triggers a fetch if the uid hasn't been fetched yet.
 *
 *   this.profileColorService.ensureLoadedMany(uids);
 *   // Batch version.
 */
@Injectable({ providedIn: 'root' })
export class ProfileColorService {
    private afs = inject(AngularFirestore);

    /** uid → profileColor (hex string with #, e.g. '#e53935') */
    private cache = new Map<string, string>();

    /** Track in-flight fetches to avoid duplicate requests */
    private pending = new Set<string>();

    /**
     * Get the cached profile color for a user.
     * Returns the hex color string or empty string if not loaded / not set.
     */
    getColor(uid: string): string {
        return this.cache.get(uid) || '';
    }

    /**
     * Ensure a single user's profile color is loaded.
     * If already cached or in-flight, this is a no-op.
     */
    ensureLoaded(uid: string): void {
        if (!uid || this.cache.has(uid) || this.pending.has(uid)) return;
        this.pending.add(uid);

        this.afs
            .doc(`users/${uid}`)
            .get()
            .pipe(take(1))
            .subscribe({
                next: (doc) => {
                    const data = doc.data() as Record<string, unknown> | undefined;
                    this.cache.set(uid, (data?.['profileColor'] as string) || '');
                    this.pending.delete(uid);
                },
                error: () => {
                    this.cache.set(uid, '');
                    this.pending.delete(uid);
                },
            });
    }

    /**
     * Ensure multiple users' profile colors are loaded.
     */
    ensureLoadedMany(uids: string[]): void {
        for (const uid of uids) {
            this.ensureLoaded(uid);
        }
    }

    /**
     * Seed the cache from orgUsers data that already contains profileColor.
     * This avoids individual Firestore fetches for users whose color is
     * already available on the organization document.
     *
     * Users without a profileColor in orgUsers are returned so the caller
     * can fall back to ensureLoadedMany() for them.
     */
    seedFromOrgUsers(orgUsers: Record<string, any>): string[] {
        const missing: string[] = [];
        const userKeys: string[] = orgUsers?.['userKeys'] || [];
        for (const uid of userKeys) {
            if (!uid || this.cache.has(uid)) continue;
            const user = orgUsers[uid];
            if (user?.profileColor) {
                this.cache.set(uid, user.profileColor);
            } else {
                missing.push(uid);
            }
        }
        return missing;
    }

    /**
     * Build a ui-avatars URL using the user's profile color.
     * Falls back to grey (#9e9e9e) when no profile color is set.
     */
    getAvatarUrl(uid: string, name: string, size = 40): string {
        const color = this.getColor(uid).replace('#', '') || '9e9e9e';
        return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'User')}&background=${color}&color=fff&size=${size}&bold=true`;
    }
}
